import React, { useEffect, useState } from "react";
import { showallProblem, getprojectIdea,interview } from "../../api/codingProblem.js";
import {
  Code,
  CheckCircle2,
  Circle,
  Search,
  TrendingUp,
  BookOpen,
  Clock,
  Flame,
  ExternalLink,
  BarChart3,
  Brain,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

function PlacementPrep() {
  const navigator = useNavigate();
  const [activeTab, setActiveTab] = useState("problems"); // problems, companies, quizzes, progress
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("All");
  const [selectedDifficulty, setSelectedDifficulty] = useState("All");
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectIdeas, setProjectIdeas] = useState([]);
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  // Sample quizzes data
 const quizzes = [
   {
     title: "Data Structures Fundamentals",
     questions: 25,
     duration: "30 min",
     difficulty: "Easy",
     topic: "DSA",
     completed: false,
   },
   {
     title: "Operating System Concepts",
     questions: 20,
     duration: "30 min",
     difficulty: "Medium",
     topic: "Operating System",
     completed: false,
   },
   {
     title: "DBMS & SQL Queries",
     questions: 30,
     duration: "45 min",
     difficulty: "Medium",
     topic: "DBMS",
     completed: false,
   },
   {
     title: "Computer Networks Basics",
     questions: 25,
     duration: "35 min",
     difficulty: "Medium",
     topic: "Computer Networks",
     completed: false,
   },
   {
     title: "JavaScript Core Concepts",
     questions: 30,
     duration: "40 min",
     difficulty: "Easy",
     topic: "JavaScript",
     completed: true,
   },
   {
     title: "React.js Advanced",
     questions: 20,
     duration: "35 min",
     difficulty: "Medium",
     topic: "React",
     completed: false,
   },
   {
     title: "Node.js Backend Fundamentals",
     questions: 25,
     duration: "40 min",
     difficulty: "Medium",
     topic: "Node.js",
     completed: false,
   },
   {
     title: "Python Programming",
     questions: 30,
     duration: "45 min",
     difficulty: "Easy",
     topic: "Python",
     completed: false,
   },
   {
     title: "Java Core & OOP",
     questions: 30,
     duration: "45 min",
     difficulty: "Medium",
     topic: "Java",
     completed: false,
   },
   {
     title: "C++ Programming & STL",
     questions: 30,
     duration: "45 min",
     difficulty: "Medium",
     topic: "C++",
     completed: false,
   },
   {
     title: "Theory of Automata",
     questions: 20,
     duration: "30 min",
     difficulty: "Hard",
     topic: "Theory of Automata",
     completed: false,
   },
   {
     title: "Compiler Design",
     questions: 20,
     duration: "30 min",
     difficulty: "Hard",
     topic: "Compiler Design",
     completed: false,
   },
   {
     title: "Cloud Computing",
     questions: 25,
     duration: "40 min",
     difficulty: "Medium",
     topic: "Cloud Computing",
     completed: false,
   },
   {
     title: "Cybersecurity Fundamentals",
     questions: 25,
     duration: "40 min",
     difficulty: "Medium",
     topic: "Cybersecurity",
     completed: false,
   },
   {
     title: "Data Science Basics",
     questions: 30,
     duration: "45 min",
     difficulty: "Medium",
     topic: "Data Science",
     completed: false,
   },
   {
     title: "AI & Machine Learning",
     questions: 30,
     duration: "50 min",
     difficulty: "Hard",
     topic: "AI/ML",
     completed: false,
   },
   {
     title: "General Computer Science",
     questions: 20,
     duration: "30 min",
     difficulty: "Easy",
     topic: "Other",
     completed: false,
   },
 ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await showallProblem();
      const projectResponse = await getprojectIdea();
      const interviewResponse = await interview();
      setInterviewQuestions(interviewResponse.data.data || []);
      setProblems(response.data.problem || []);
      setProjectIdeas(projectResponse.data.projects || []);
    } catch (error) {
      console.error("Error fetching problems:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Get unique topics
  const topics = ["All", ...new Set(problems.map((p) => p.topic))];

  // Filter problems
  const filteredProblems = problems.filter((item) => {
    const matchesSearch =
      item.problem?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.topic?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTopic =
      selectedTopic === "All" || item.topic === selectedTopic;
    const matchesDifficulty =
      selectedDifficulty === "All" || item.level === selectedDifficulty;
    return matchesSearch && matchesTopic && matchesDifficulty;
  });

  // Calculate stats
  const solvedCount = problems.filter((p) => p.status).length;
  const totalCount = problems.length;
  const progressPercentage =
    totalCount > 0 ? Math.round((solvedCount / totalCount) * 100) : 0;

  const toggleStatus = (id) => {
    setProblems((prev) =>
      prev.map((item) =>
        item._id === id ? { ...item, status: !item.status } : item
      )
    );
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "easy":
        return "text-green-600 bg-green-50";
      case "medium":
        return "text-yellow-600 bg-yellow-50";
      case "hard":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            🎯 Placement Preparation Hub
          </h1>
          <p className="text-gray-600">
            Master coding problems, ace company interviews, and track your
            progress
          </p>
        </div>


        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md mb-6 overflow-hidden">
          <div className="flex overflow-x-auto">
            {[
              { id: "problems", label: "Coding Problems", icon: Code },
              { id: "quizzes", label: "Quizzes", icon: Brain },
              { id: "interviews", label: "Interviews Questions", icon: Clock },
              { id: "projects", label: "Project Ideas", icon: BookOpen },

            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-6 py-4 font-medium transition-all ${
                    activeTab === tab.id
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <Icon size={20} />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Problems Tab */}
        {activeTab === "problems" && (
          <div>
            {/* Filters */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <input
                    type="text"
                    placeholder="Search problems or topics..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>

                {/* Topic Filter */}
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                >
                  {topics.map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>

                {/* Difficulty Filter */}
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                >
                  <option value="All">All Difficulties</option>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
            </div>

            {/* Problems Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left font-semibold">
                        Problem
                      </th>
                      <th className="px-6 py-4 text-left font-semibold">
                        Topic
                      </th>
                      <th className="px-6 py-4 text-left font-semibold">
                        Difficulty
                      </th>
                      <th className="px-6 py-4 text-left font-semibold">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProblems.map((item, index) => (
                      <tr
                        key={item._id}
                        className={`border-b hover:bg-blue-50 transition-colors ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="px-6 py-4">
                          <button
                            onClick={() => toggleStatus(item._id)}
                            className="focus:outline-none"
                          >
                            {item.status ? (
                              <CheckCircle2 className="w-6 h-6 text-green-500" />
                            ) : (
                              <Circle className="w-6 h-6 text-gray-300" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900">
                          {item.problem}
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            {item.topic}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${getDifficultyColor(item.level)}`}
                          >
                            {item.level}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all text-sm font-medium"
                          >
                            <span>Solve</span>
                            <ExternalLink size={16} />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredProblems.length === 0 && (
                <div className="text-center py-12">
                  <Code className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No problems found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quizzes Tab */}
        {activeTab === "quizzes" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quizzes.map((quiz, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {quiz.title}
                    </h3>
                    <p className="text-sm text-gray-600">{quiz.topic}</p>
                  </div>
                  {quiz.completed && (
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <BookOpen size={18} />
                    <span className="text-sm">{quiz.questions} Questions</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Clock size={18} />
                    <span className="text-sm">{quiz.duration}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${getDifficultyColor(quiz.difficulty)}`}
                  >
                    {quiz.difficulty}
                  </span>
                  <button className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
                    onClick={() => {
                    navigator(`/quiz/${quiz.topic.toLowerCase()}`);
                  }}
                  >
                    {quiz.completed ? "Retake" : "Start Quiz"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Project tab */}
        {activeTab === "projects" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projectIdeas.length > 0 ? (
              projectIdeas.map((idea) => (
                <div
                  key={idea._id}
                  className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border"
                >
                  {/* Title + Level */}
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-xl font-bold text-gray-900">
                      {idea.title}
                    </h3>
                    <span className="text-xs px-3 py-1 rounded-full bg-red-100 text-red-600 font-medium">
                      {idea.level}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4">
                    {idea.description}
                  </p>

                  {/* Tech Stack */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">
                      Tech Stack
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {idea.techStack?.map((tech, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-md"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">
                      Key Features
                    </h4>
                    <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
                      {idea.features?.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Tags */}
                  <div className="mb-4 flex flex-wrap gap-2">
                    {idea.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-between items-center mt-4">
                    <a
                      href={idea.githubSample}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-blue-600 hover:underline"
                    >
                      View GitHub →
                    </a>

                    <button className="bg-black text-white text-sm px-4 py-2 rounded-lg hover:bg-gray-800 transition">
                      Start Project
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-lg">
                No project ideas available.
              </p>
            )}
          </div>
        )}
        {/* interview tab */}

        {activeTab === "interviews" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interviewQuestions.map((q, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                {/* Header */}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-medium px-3 py-1 bg-blue-100 text-blue-600 rounded-full">
                    {q.role}
                  </span>

                  <span
                    className={`text-xs font-medium px-3 py-1 rounded-full ${
                      q.difficulty === "Hard"
                        ? "bg-red-100 text-red-600"
                        : q.difficulty === "Medium"
                          ? "bg-yellow-100 text-yellow-600"
                          : "bg-green-100 text-green-600"
                    }`}
                  >
                    {q.difficulty}
                  </span>
                </div>

                {/* Question */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {q.question}
                </h3>

                {/* Answer */}
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {q.answer}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {q.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>{q.company}</span>
                  <span>{q.experienceLevel}</span>
                </div>
              </div>
            ))}
          </div>
        )}  
        {/* Progress Tab */}
    
      </div>
    </div>
  );
}

export default PlacementPrep;
